import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Sparkles, Loader2, Plus, Trash2, HelpCircle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const EMPTY_Q = () => ({ question: "", options: ["", "", "", ""], correct_index: 0, explanation: "" });

export default function CreateQuizModal({ courses, onClose, onCreated }) {
  const [courseId, setCourseId] = useState(courses[0]?.id || "");
  const [videos, setVideos] = useState([]);
  const [videoId, setVideoId] = useState("");
  const [mode, setMode] = useState("ai");
  const [manualQs, setManualQs] = useState([EMPTY_Q()]);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!courseId) return;
    api.get(`/courses/${courseId}`).then((r) => { setVideos(r.data.videos); setVideoId(r.data.videos[0]?.id || ""); });
  }, [courseId]);

  const generateWithAI = async () => {
    if (!videoId) { toast.error("Pick a lecture first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/courses/${courseId}/quizzes/generate-ai`, { video_id: videoId });
      toast.success(`AI generated ${data.questions.length} questions!`);
      onCreated();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI generation failed");
    } finally {
      setBusy(false);
    }
  };

  const createManual = async () => {
    if (!title.trim()) { toast.error("Add a quiz title"); return; }
    setBusy(true);
    try {
      await api.post(`/courses/${courseId}/quizzes`, { title, video_id: videoId, questions: manualQs });
      toast.success("Quiz created!");
      onCreated();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to create quiz");
    } finally {
      setBusy(false);
    }
  };

  const updateQ = (i, patch) => setManualQs((qs) => qs.map((q, qi) => (qi === i ? { ...q, ...patch } : q)));
  const updateOpt = (i, oi, val) => setManualQs((qs) => qs.map((q, qi) => (qi === i ? { ...q, options: q.options.map((o, x) => (x === oi ? val : o)) } : q)));

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4" data-testid="create-quiz-modal">
      <div className="glass-panel rounded-2xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-white text-sm flex items-center gap-2"><HelpCircle className="w-4 h-4 text-indigo-300" /> Create Quiz</h3>
          <button onClick={onClose} data-testid="create-quiz-close-btn" className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Course</label>
        <select value={courseId} onChange={(e) => setCourseId(e.target.value)} data-testid="create-quiz-course-select" className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
          {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>

        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mt-3 block">Lecture</label>
        <select value={videoId} onChange={(e) => setVideoId(e.target.value)} data-testid="create-quiz-video-select" className="w-full mt-1.5 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
          {videos.map((v) => <option key={v.id} value={v.id}>{v.title}</option>)}
        </select>

        <div className="flex items-center gap-1 bg-white/5 rounded-lg p-1 mt-4">
          <button onClick={() => setMode("ai")} data-testid="create-quiz-mode-ai-btn" className={`flex-1 text-xs font-semibold py-2 rounded-md ${mode === "ai" ? "bg-indigo-500 text-white" : "text-slate-400"}`}>Generate with AI</button>
          <button onClick={() => setMode("manual")} data-testid="create-quiz-mode-manual-btn" className={`flex-1 text-xs font-semibold py-2 rounded-md ${mode === "manual" ? "bg-indigo-500 text-white" : "text-slate-400"}`}>Write Manually</button>
        </div>

        {mode === "ai" ? (
          <>
            <p className="text-xs text-slate-400 mt-3">AI reads this lecture's transcript/notes and writes 5 comprehension questions automatically.</p>
            <button onClick={generateWithAI} disabled={busy} data-testid="create-quiz-generate-ai-btn" className="w-full mt-4 flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-500 to-violet-500 hover:opacity-90 disabled:opacity-60 text-white text-sm font-semibold py-2.5 rounded-lg">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate Quiz with AI
            </button>
          </>
        ) : (
          <>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Quiz title" data-testid="create-quiz-title-input" className="w-full mt-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500" />
            {manualQs.map((q, i) => (
              <div key={i} className="mt-3 bg-white/[0.03] rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold text-slate-400">Question {i + 1}</span>
                  {manualQs.length > 1 && <button onClick={() => setManualQs((qs) => qs.filter((_, x) => x !== i))} data-testid={`create-quiz-remove-q-${i}`}><Trash2 className="w-3.5 h-3.5 text-rose-400" /></button>}
                </div>
                <input value={q.question} onChange={(e) => updateQ(i, { question: e.target.value })} placeholder="Question text" data-testid={`create-quiz-question-input-${i}`} className="w-full mt-2 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white" />
                {q.options.map((o, oi) => (
                  <div key={oi} className="flex items-center gap-2 mt-1.5">
                    <input type="radio" checked={q.correct_index === oi} onChange={() => updateQ(i, { correct_index: oi })} data-testid={`create-quiz-correct-radio-${i}-${oi}`} />
                    <input value={o} onChange={(e) => updateOpt(i, oi, e.target.value)} placeholder={`Option ${oi + 1}`} data-testid={`create-quiz-option-input-${i}-${oi}`} className="flex-1 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white" />
                  </div>
                ))}
              </div>
            ))}
            <button onClick={() => setManualQs((qs) => [...qs, EMPTY_Q()])} data-testid="create-quiz-add-question-btn" className="w-full mt-2 flex items-center justify-center gap-1.5 text-xs font-semibold text-indigo-300 border border-dashed border-indigo-400/30 rounded-lg py-2">
              <Plus className="w-3.5 h-3.5" /> Add Question
            </button>
            <button onClick={createManual} disabled={busy} data-testid="create-quiz-save-manual-btn" className="w-full mt-4 flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-semibold py-2.5 rounded-lg">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Quiz"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
